import { Fragment } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getIDFromUrl } from '@rossum/api-client';
import { Annotation } from '@rossum/api-client/annotations';
import { DocumentRelation } from '@rossum/api-client/documentRelations';
import { DetailDrawer } from '@rossum/ui';
import { IconCloudDownload } from '@rossum/ui/icons/tabler';
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  SvgIcon,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { isEmbedded } from '../../../constants/config';
import { annotationSideloadsSelector } from '../../../redux/modules/annotation/selectors';
import { safeOrganizationSelector } from '../../../redux/modules/organization/selectors';
import { isUserAnnotatorBetaSelector } from '../../../redux/modules/user/selectors';
import { useGuardedDownloadOriginalDocuments } from '../hooks/useGuardedDownloadOriginalDocuments';
import { useFetchDocuments } from './hooks/useFetchDocuments';

type FilesDrawerProps = {
  annotation: Annotation;
  documentRelations: DocumentRelation[];
  onClose: () => void;
};

export const FilesDrawer = ({
  annotation,
  documentRelations,
  onClose,
}: FilesDrawerProps) => {
  const intl = useIntl();
  const downloadOriginalDocuments = useGuardedDownloadOriginalDocuments();
  const annotationSideloads = useSelector(annotationSideloadsSelector);
  const originalDocument = annotationSideloads.document;

  const disableDownloadOriginal =
    useSelector(safeOrganizationSelector)?.uiSettings?.features
      ?.disable_download_original ?? false;
  const isUserAnnotatorBeta = useSelector(isUserAnnotatorBetaSelector);

  const downloadVisible =
    (isEmbedded() && !disableDownloadOriginal) ||
    (!isEmbedded() && !disableDownloadOriginal && !isUserAnnotatorBeta);

  // TODO: ask BE for documentRelations?sidelaod=documents
  const documentsIds = documentRelations.flatMap(({ documents }) =>
    documents.map(d => getIDFromUrl(d))
  );

  const { data } = useFetchDocuments({ documentIds: documentsIds });

  const documents = [
    data?.results ? data.results : [],
    originalDocument ?? [],
  ].flat();

  const releationsDocuments = R.indexBy(documents ?? [], item => item.url);

  const originalDocumentRelation = originalDocument
    ? {
        type: 'orginal',
        key: 'original_key',
        id: 'original_id',
        documents: [originalDocument.url],
        annotation: annotation.url,
      }
    : undefined;

  const relations = [
    originalDocumentRelation ?? [],
    ...documentRelations,
  ].flat();

  return (
    <DetailDrawer
      open
      onClose={onClose}
      title={intl.formatMessage({
        id: 'components.fileDrawer.title',
      })}
    >
      <List sx={{ padding: 2 }}>
        {relations.map(relation => (
          <Fragment key={relation.id}>
            {relation.documents.map(documentUrl => {
              const document = releationsDocuments[documentUrl];
              if (!document) return null;

              return (
                <ListItem
                  key={document.id}
                  secondaryAction={
                    downloadVisible ? (
                      <IconButton
                        className="hover-button"
                        edge="end"
                        onClick={() =>
                          downloadOriginalDocuments([document], {
                            type: 'document',
                          })
                        }
                      >
                        <SvgIcon fontSize="medium">
                          <IconCloudDownload />
                        </SvgIcon>
                      </IconButton>
                    ) : null
                  }
                  divider
                  sx={{
                    '& .hover-button': {
                      opacity: 0,
                      transition: 'opacity 0.4s',
                    },
                    '&:hover .hover-button': {
                      opacity: 1,
                    },
                    bgcolor: 'action.hover',
                    mb: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: borderRadius('s'),
                  }}
                >
                  <ListItemText primary={document.originalFileName} />
                </ListItem>
              );
            })}
          </Fragment>
        ))}
      </List>
    </DetailDrawer>
  );
};
